import { useEffect, useRef, useState } from "react";
import { customFetch } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

export interface UseSectionAutoSaveOptions<T extends Record<string, any>> {
  form: T;
  serverData: T | undefined;
  sections: Record<string, string[]>;
  endpoint: string;
  method?: "PUT" | "PATCH";
  enabled: boolean;
  debounceMs?: number;
  queryKey?: readonly unknown[];
  // `fields` = os campos MEXIDOS da seção (não a seção inteira); quem implementa põe os que precisam ir junto.
  buildPayload?: (
    section: string,
    fields: string[],
    form: T,
  ) => Record<string, any> | null;
  validate?: (section: string, form: T) => string | null;
  onError?: (section: string, err: any) => void;
  // Dispara após um save bem-sucedido de uma seção. Útil pra sincronizar
  // caches que dependem dos mesmos dados (ex.: o getMe que alimenta a navbar).
  onSaved?: (section: string, response: any) => void;
  applyServerResponse?: boolean;
}

// Proteção contra race conditions de auto-save por seção:
// - `abort`: cancela a request em voo da MESMA seção quando uma nova
//   alteração chega antes da resposta anterior, evitando que uma resposta
//   atrasada sobrescreva o estado mais recente.
// - `seq`: número de série monotônico — respostas com `mySeq < state.seq`
//   são descartadas (defesa em profundidade caso o abort não chegue a
//   tempo no servidor / fetch já tenha resolvido).
// Seções diferentes não compartilham campos, então podem salvar em
// paralelo sem mutex global.
interface SectionState {
  timer: ReturnType<typeof setTimeout> | null;
  abort: AbortController | null;
  seq: number;
  inflight: number;
  errored: boolean;
}

function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (!deepEqual(a[k], b[k])) return false;
  return true;
}

export interface UseSectionAutoSaveReturn {
  status: AutoSaveStatus;
  pendingSections: string[];
  flush: (section?: string) => Promise<void>;
  // Devolve os campos que mudaram NO SERVIDOR e que a pessoa não mexeu nesta tela: quem chama põe no formulário.
  acceptServerSnapshot: (snapshot: Record<string, any>) => Record<string, any>;
  markFieldSaved: (field: string, value: any) => void;
}

export function useSectionAutoSave<T extends Record<string, any>>(
  opts: UseSectionAutoSaveOptions<T>,
): UseSectionAutoSaveReturn {
  const {
    form,
    serverData,
    sections,
    endpoint,
    method = "PUT",
    enabled,
    debounceMs = 700,
    queryKey,
    buildPayload,
    validate,
    onError,
    onSaved,
    applyServerResponse = true,
  } = opts;

  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const [pendingSections, setPendingSections] = useState<string[]>([]);

  const lastSavedRef = useRef<Record<string, any>>({});
  // O que o servidor devolveu, campo a campo, no NOSSO último salvamento. Ele normaliza (descarta nome vazio, …):
  // "Outras formas de retirada" ligada com o nome ainda vazio sai [""] e volta null. A releitura que trouxer
  // exatamente isso é o eco deste salvamento, não mudança feita por fora (ver acceptServerSnapshot).
  const ecoRef = useRef<Record<string, any>>({});
  const initializedRef = useRef(false);
  const sectionStateRef = useRef<Record<string, SectionState>>({});
  const formRef = useRef(form);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionsRef = useRef(sections);
  const buildPayloadRef = useRef(buildPayload);
  const validateRef = useRef(validate);
  const onErrorRef = useRef(onError);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    formRef.current = form;
  }, [form]);
  useEffect(() => {
    sectionsRef.current = sections;
  });
  useEffect(() => {
    buildPayloadRef.current = buildPayload;
    validateRef.current = validate;
    onErrorRef.current = onError;
    onSavedRef.current = onSaved;
  });

  useEffect(() => {
    if (!serverData || initializedRef.current) return;
    const snap: Record<string, any> = {};
    for (const sec of Object.keys(sectionsRef.current)) {
      for (const f of sectionsRef.current[sec]) {
        snap[f] = (serverData as any)[f];
      }
    }
    lastSavedRef.current = snap;
    initializedRef.current = true;
  }, [serverData]);

  const ensureSection = (sec: string): SectionState => {
    if (!sectionStateRef.current[sec]) {
      sectionStateRef.current[sec] = {
        timer: null,
        abort: null,
        seq: 0,
        inflight: 0,
        errored: false,
      };
    }
    return sectionStateRef.current[sec];
  };

  // `gravou` = false quando a seção terminou sem mandar nada (ex.: o WhatsApp ainda incompleto, que não vai): sai do
  // «Salvando…» sem dizer «Salvo».
  const refreshAggregateStatus = (gravou = true) => {
    const states = Object.entries(sectionStateRef.current);
    const pending: string[] = [];
    let anyInflight = false;
    let anyError = false;
    for (const [sec, st] of states) {
      if (st.inflight > 0 || st.timer) {
        anyInflight = true;
        pending.push(sec);
      }
      if (st.errored) anyError = true;
    }
    setPendingSections(pending);
    if (anyInflight) {
      setStatus("saving");
      return;
    }
    if (anyError) {
      setStatus("error");
      return;
    }
    setStatus((prev) => {
      if (prev === "saving") {
        if (!gravou) return "idle";
        if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = setTimeout(
          () => setStatus("idle"),
          1500,
        );
        return "saved";
      }
      return prev;
    });
  };

  const saveSection = async (section: string): Promise<void> => {
    const fields = sectionsRef.current[section];
    if (!fields) return;
    const state = ensureSection(section);

    if (validateRef.current) {
      const errMsg = validateRef.current(section, formRef.current);
      if (errMsg) {
        state.errored = true;
        onErrorRef.current?.(section, new Error(errMsg));
        refreshAggregateStatus();
        return;
      }
    }

    // Só os campos que a pessoa mexeu (diferentes do último carregado/salvo). Mandar a seção inteira levava junto o
    // valor velho de campo mudado por fora depois que a tela abriu (JHON SUSHI, 19/10/2026: o endereço manual salvo
    // desfez o tipo de taxa que a Configuradora tinha acabado de trocar). Quem chama põe os que andam junto.
    const mexidos = fields.filter((f) => !deepEqual((formRef.current as any)[f], lastSavedRef.current[f]));
    if (mexidos.length === 0) {
      refreshAggregateStatus(false);
      return;
    }
    const enviados: Record<string, any> = {};
    for (const f of mexidos) enviados[f] = (formRef.current as any)[f];

    let payload: Record<string, any> | null;
    if (buildPayloadRef.current) {
      payload = buildPayloadRef.current(section, mexidos, formRef.current);
    } else {
      payload = { ...enviados };
    }
    if (!payload || Object.keys(payload).length === 0) {
      refreshAggregateStatus(false);
      return;
    }
    const foram = mexidos.filter((f) => f in payload!);

    if (state.abort) {
      try {
        state.abort.abort();
      } catch {}
    }
    state.abort = new AbortController();
    state.seq += 1;
    state.inflight += 1;
    state.errored = false;
    const mySeq = state.seq;
    refreshAggregateStatus();

    try {
      const response = await customFetch<any>(endpoint, {
        method,
        body: JSON.stringify(payload),
        signal: state.abort.signal,
      });

      if (mySeq < state.seq) {
        return;
      }

      // Salvo = o que FOI (o valor do envio): campo mexido de novo durante a ida continua pendente e vai no próximo.
      const snap = { ...lastSavedRef.current };
      for (const f of foram) {
        snap[f] = enviados[f];
      }
      lastSavedRef.current = snap;
      if (response && typeof response === "object") {
        for (const f of foram) {
          if (f in response) ecoRef.current[f] = response[f];
          else delete ecoRef.current[f];
        }
      }

      if (applyServerResponse && queryKey && response && typeof response === "object") {
        queryClient.setQueryData(queryKey, (prev: any) =>
          prev ? { ...prev, ...response } : response,
        );
      }

      onSavedRef.current?.(section, response);
    } catch (err: any) {
      const isAbort =
        err?.name === "AbortError" ||
        err?.code === "ERR_CANCELED" ||
        err?.message === "The user aborted a request." ||
        (typeof err?.message === "string" &&
          err.message.toLowerCase().includes("abort"));
      if (isAbort) return;
      if (mySeq < state.seq) return;
      if (err?.status === 401) return;
      state.errored = true;
      onErrorRef.current?.(section, err);
    } finally {
      state.inflight = Math.max(0, state.inflight - 1);
      refreshAggregateStatus();
    }
  };

  const scheduleSection = (section: string) => {
    const state = ensureSection(section);
    if (state.timer) clearTimeout(state.timer);
    setStatus("saving");
    state.timer = setTimeout(() => {
      state.timer = null;
      void saveSection(section);
    }, debounceMs);
    refreshAggregateStatus();
  };

  useEffect(() => {
    if (!enabled || !initializedRef.current) return;
    for (const sec of Object.keys(sectionsRef.current)) {
      const fields = sectionsRef.current[sec];
      const changed = fields.some(
        (f) => !deepEqual((form as any)[f], lastSavedRef.current[f]),
      );
      if (changed) {
        scheduleSection(sec);
      }
    }
  }, [form, enabled]);

  useEffect(
    () => () => {
      for (const st of Object.values(sectionStateRef.current)) {
        if (st.timer) clearTimeout(st.timer);
        if (st.abort) {
          try {
            st.abort.abort();
          } catch {}
        }
      }
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    },
    [],
  );

  const flush = async (section?: string) => {
    if (section) {
      const state = ensureSection(section);
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
        await saveSection(section);
      }
      return;
    }
    const promises: Promise<void>[] = [];
    for (const sec of Object.keys(sectionStateRef.current)) {
      const state = sectionStateRef.current[sec];
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
        promises.push(saveSection(sec));
      }
    }
    await Promise.all(promises);
  };

  // Opção mudada POR FORA (Atendente, Configuradora, outro aparelho, outra aba) chega aqui na releitura. Antes só
  // a referência do "salvo" andava e o formulário ficava com o valor velho: na próxima mexida em qualquer campo, a
  // seção inteira era regravada com o velho e desfazia a mudança (Koma, 05/10/2026: a data única ligada às 11:46
  // caiu às 11:51 junto com outras 6 opções). Agora o campo que a pessoa NÃO mexeu nesta tela (formulário igual
  // ao último salvo) acompanha o servidor; o que ela mexeu continua sendo dela.
  const acceptServerSnapshot = (snapshot: Record<string, any>): Record<string, any> => {
    const next = { ...lastSavedRef.current };
    const rebase: Record<string, any> = {};
    for (const sec of Object.keys(sectionsRef.current)) {
      const fields = sectionsRef.current[sec];
      const state = sectionStateRef.current[sec];
      const sectionBusy = state && (state.inflight > 0 || !!state.timer);
      if (sectionBusy) continue;
      for (const f of fields) {
        if (!(f in snapshot)) continue;
        const anterior = lastSavedRef.current[f];
        if (!deepEqual(snapshot[f], anterior)) {
          // Eco do nosso salvamento (o servidor guardou a versão normalizada do que mandamos): o formulário fica
          // como está. Doceria kadu, 09/10/2026: a chave de retirada desligava sozinha logo depois do "Salvo".
          if (f in ecoRef.current && deepEqual(snapshot[f], ecoRef.current[f])) continue;
          delete ecoRef.current[f];
          if (deepEqual((formRef.current as any)[f], anterior)) rebase[f] = snapshot[f];
        }
        next[f] = snapshot[f];
      }
    }
    lastSavedRef.current = next;
    return rebase;
  };

  const markFieldSaved = (field: string, value: any) => {
    lastSavedRef.current = { ...lastSavedRef.current, [field]: value };
    delete ecoRef.current[field];
  };

  return { status, pendingSections, flush, acceptServerSnapshot, markFieldSaved };
}
